import { Skeleton } from "@/shared/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { cn } from "@/shared/libs/utils";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  RowSelectionState,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { useState, useRef, useEffect } from "react";
import { DataTablePagination } from "./DataTablePagination";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  totalCount?: number;
  page: number;
  pageSize: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
  manualSorting?: boolean;
  isLoading?: boolean;
  isError?: boolean;
  toolbar?: React.ReactNode;
  enableRowSelection?: boolean;
  onRowSelectionChange?: (selectedRows: TData[]) => void;
  getRowId?: (row: TData) => string;
  onRowClick?: (row: TData) => void;
  rowSelectionTrigger?: "row" | "checkbox";
}

export function DataTable<TData, TValue>({
  columns,
  data,
  totalCount,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  sorting: controlledSorting,
  onSortingChange,
  manualSorting,
  isLoading,
  isError,
  toolbar,
  enableRowSelection = false,
  onRowSelectionChange,
  getRowId,
  onRowClick,
  rowSelectionTrigger = "row",
}: DataTableProps<TData, TValue>) {
  const [internalSorting, setInternalSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [columnSizing, setColumnSizing] = useState({});
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // 정렬이 제어되는지 여부
  const isControlledSorting = controlledSorting !== undefined && onSortingChange !== undefined;
  const sorting = isControlledSorting ? controlledSorting : internalSorting;

  const handleSortingChange = (updater: SortingState | ((old: SortingState) => SortingState)) => {
    const newSorting = typeof updater === "function" ? updater(sorting) : updater;

    if (isControlledSorting) {
      onSortingChange?.(newSorting);
      // 정렬 변경 시 첫 페이지로 이동
      if (onPageChange && JSON.stringify(sorting) !== JSON.stringify(newSorting)) {
        onPageChange(1);
      }
    } else {
      setInternalSorting(newSorting);
    }
  };

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: isControlledSorting ? undefined : getSortedRowModel(),
    onSortingChange: handleSortingChange,
    onRowSelectionChange: (updater) => {
      const newSelection = typeof updater === "function" ? updater(rowSelection) : updater;
      setRowSelection(newSelection);

      if (onRowSelectionChange) {
        const selectedRows = Object.keys(newSelection)
          .filter((key) => newSelection[key])
          .map((key) => {
            // getRowId가 정의되어 있으면 key는 row ID이므로 데이터에서 찾아야 함
            if (getRowId) {
              return data.find((row) => getRowId(row) === key);
            }
            // getRowId가 없으면 key는 인덱스
            return data[parseInt(key)];
          })
          .filter((row): row is TData => row !== undefined);
        onRowSelectionChange(selectedRows);
      }
    },
    onColumnSizingChange: setColumnSizing,
    columnResizeMode: "onChange",
    defaultColumn: {
      minSize: 40,
      size: 150,
    },
    state: {
      sorting,
      rowSelection,
      columnSizing,
    },
    enableRowSelection,
    getRowId: getRowId ? (row) => getRowId(row) : undefined,
    manualPagination: true,
    manualSorting: manualSorting ?? isControlledSorting,
    pageCount: totalCount ? Math.ceil(totalCount / pageSize) : -1,
  });

  // 초기 로딩 시 컨테이너 너비에 맞춰 각 컬럼의 너비를 계산하여 설정
  useEffect(() => {
    if (!isInitialized && tableContainerRef.current && table.getAllColumns().length > 0) {
      const containerWidth = tableContainerRef.current.offsetWidth;
      const allColumns = table.getAllColumns();

      // 고정 너비 컬럼들의 너비 합계 계산
      const fixedWidthTotal = allColumns.reduce((sum, column) => {
        const columnDef = column.columnDef;
        // size === minSize === maxSize 이면 고정 너비 컬럼
        if (
          columnDef.size &&
          columnDef.size === columnDef.minSize &&
          columnDef.size === columnDef.maxSize
        ) {
          return sum + columnDef.size;
        }
        return sum;
      }, 0);

      // 가변 너비 컬럼들
      const flexibleColumns = allColumns.filter((column) => {
        const columnDef = column.columnDef;
        return !(
          columnDef.size &&
          columnDef.size === columnDef.minSize &&
          columnDef.size === columnDef.maxSize
        );
      });

      // 남은 공간을 가변 컬럼들이 균등하게 나눔
      const remainingWidth = containerWidth - fixedWidthTotal;
      const flexColumnWidth =
        flexibleColumns.length > 0 ? Math.floor(remainingWidth / flexibleColumns.length) : 150;

      const initialSizing: Record<string, number> = {};
      allColumns.forEach((column) => {
        const columnDef = column.columnDef;
        // 고정 너비 컬럼은 정의된 크기 사용
        if (
          columnDef.size &&
          columnDef.size === columnDef.minSize &&
          columnDef.size === columnDef.maxSize
        ) {
          initialSizing[column.id] = columnDef.size;
        } else {
          // 가변 너비 컬럼은 계산된 크기 사용
          initialSizing[column.id] = Math.max(flexColumnWidth, column.columnDef.minSize ?? 40);
        }
      });

      setColumnSizing(initialSizing);
      setIsInitialized(true);
    }
  }, [table, isInitialized]);

  const totalPages = totalCount ? Math.ceil(totalCount / pageSize) : 0;

  if (isError) {
    return (
      <div className="flex h-64 items-center justify-center rounded-md border">
        <div className="text-center">
          <p className="text-lg font-medium text-destructive">데이터를 불러오는데 실패했습니다.</p>
          <p className="text-sm text-muted-foreground">잠시 후 다시 시도해주세요.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {toolbar}

      <div ref={tableContainerRef} className="rounded-md border overflow-x-auto">
        <Table
          style={{
            width: table.getTotalSize(),
            tableLayout: "fixed",
          }}
        >
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      className="bg-muted/40 overflow-hidden"
                      style={{
                        width: `${header.getSize()}px`,
                        minWidth: `${header.column.columnDef.minSize ?? 40}px`,
                        maxWidth: header.column.columnDef.maxSize
                          ? `${header.column.columnDef.maxSize}px`
                          : undefined,
                        position: "relative",
                      }}
                    >
                      <div className="truncate">
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </div>
                      {/* 리사이저 */}
                      {header.column.getCanResize() && (
                        <div
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          className={cn(
                            "absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none",
                            "bg-muted-foreground/20 hover:bg-muted-foreground/40 transition-colors",
                            header.column.getIsResizing() && "bg-primary/60"
                          )}
                          style={{
                            userSelect: "none",
                          }}
                        />
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              // Loading skeleton
              Array.from({ length: pageSize }).map((_, index) => (
                <TableRow key={index}>
                  {columns.map((_, cellIndex) => (
                    <TableCell key={cellIndex}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => {
                const clickable =
                  !!onRowClick || (enableRowSelection && rowSelectionTrigger === "row");

                return (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                    className={cn(clickable && "cursor-pointer")}
                    onClick={() => {
                      if (onRowClick) {
                        onRowClick(row.original);
                        return;
                      }
                      if (enableRowSelection && rowSelectionTrigger === "row") {
                        row.toggleSelected();
                      }
                    }}
                  >
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <TableCell
                          key={cell.id}
                          className="truncate overflow-hidden"
                          style={{
                            width: `${cell.column.getSize()}px`,
                            minWidth: `${cell.column.columnDef.minSize ?? 40}px`,
                            maxWidth: cell.column.columnDef.maxSize
                              ? `${cell.column.columnDef.maxSize}px`
                              : undefined,
                          }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  데이터가 없습니다.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <DataTablePagination
        page={page}
        pageSize={pageSize}
        totalCount={totalCount || 0}
        totalPages={totalPages}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        selectedCount={Object.keys(rowSelection).filter((key) => rowSelection[key]).length}
        totalRows={data.length}
      />
    </div>
  );
}
